import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOnboarding } from "hooks/useOnboarding";
import { OnboardingOverlay, OnboardingReplayButton } from "components/onboarding";
import { dashboardOnboarding } from "components/onboarding/steps";
import Chart from "react-apexcharts";
import car from "../../../assets/images/car.svg";
import RidesIcon from "../../../assets/svg/RidesIcon.svg";
import RevenueIcon from "../../../assets/svg/RevenueIcon.svg";
import VehiclesIcon from "../../../assets/svg/VehiclesIcon.svg";
import { useTranslation } from "react-i18next";
import { LoadingSkeleton } from "../../../components/common/OptimizedComponents";
import { LeafletMapComponent } from "../../../components/maps/LeafletMapComponent";

import Navbar from "components/navbar";
import Card from "components/card";
import Loader from "components/loader/loader";
import { LoadingSpinner as CustomSpinner } from "components/common/LoadingComponents";
import { Link, useNavigate } from "react-router-dom";
import { useAdminCache } from "../../../services/cache.service";
import { useErrorHandler } from "../../../utils/error-handling.utils";
import {
  HeatmapDataPoint,
  OnlineDriver,
  DashboardState,
} from "../../../types/dashboard.types";
import { Types } from "shared-lib";

const center = { lat: -23.5443882, lng: -46.6494069 };

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getDashboardData, getDrivers } = useAdminCache();
  const { handleApiError } = useErrorHandler();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onboarding = useOnboarding(dashboardOnboarding);

  const [state, setState] = useState<DashboardState>({
    intervalState: null,
    allOnlineDrivers: [],
    dashboardData: null,
    isLoading: false,
    isSpinner: true,
    ongoingRidesCount: 0,
    completeRidesCount: 0,
    onlineDriversCount: 0,
    totalDriver: 0,
    totalRiders: 0,
    totalRevenue: 0,
    todayRides: 0,
    weeklyData: [],
  });

  // Função otimizada para buscar dados do dashboard com cache
  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      setState(prev => ({ ...prev, isSpinner: true }));

      const dashboardData = await getDashboardData(forceRefresh);

      if (!dashboardData) {
        setState(prev => ({ ...prev, isSpinner: false }));
        return;
      }

      setState(prev => ({
        ...prev,
        dashboardData,
        ongoingRidesCount: dashboardData.activeRides || 0,
        completeRidesCount: dashboardData.completedRides || 0,
        onlineDriversCount: dashboardData.onlineDrivers || 0,
        totalDriver: dashboardData.totalDrivers || 0,
        totalRiders: dashboardData.totalRiders || 0,
        totalRevenue: dashboardData.totalRevenue || 0,
        todayRides: dashboardData.todayRides || 0,
        weeklyData: dashboardData.weeklyData || [],
        isSpinner: false
      }));
    } catch (error) {
      handleApiError(error, 'Dashboard - loadDashboardData');
      setState(prev => ({ ...prev, isSpinner: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Função otimizada para buscar drivers online com cache
  const loadOnlineDrivers = useCallback(async (forceRefresh = false) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Usar cache para drivers online com TTL de 30 segundos
      const drivers = await getDrivers(
        { page: 1, limit: 100 },
        forceRefresh
      );

      // Filtrar apenas drivers online
      const onlineDrivers = (drivers?.data ?? []).filter(driver =>
        driver.status === Types.DriverStatusEnum.ONLINE ||
        driver.status === Types.DriverStatusEnum.ON_RIDE
      );

      setState(prev => ({
        ...prev,
        allOnlineDrivers: onlineDrivers as OnlineDriver[],
        isLoading: false
      }));
    } catch (error) {
      handleApiError(error, 'Dashboard - loadOnlineDrivers');
      setState(prev => ({ ...prev, isLoading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store refs for interval functions
  const loadDashboardDataRef = useRef(loadDashboardData);
  const loadOnlineDriversRef = useRef(loadOnlineDrivers);

  useEffect(() => {
    loadDashboardDataRef.current = loadDashboardData;
    loadOnlineDriversRef.current = loadOnlineDrivers;
  }, [loadDashboardData, loadOnlineDrivers]);

  // Configurar intervalo para atualização automática
  const setupAutoRefresh = useCallback(() => {
    intervalRef.current = setInterval(() => {
      loadDashboardDataRef.current(true); // Force refresh a cada 2 minutos
      loadOnlineDriversRef.current(true); // Force refresh drivers online
    }, 120000); // 2 minutos

    setState(prev => ({ ...prev, intervalState: intervalRef.current }));
  }, []);

  // Limpar intervalo
  const clearAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setState(prev => ({ ...prev, intervalState: null }));
    }
  }, []);

  const handleNavigation = () => {
    navigate("/admin/rides?data=ongoing-rides");
  };

  const handleNavigationCompleteRide = () => {
    navigate("/admin/rides?data=completed");
  };

  // Initial data load - runs only once
  useEffect(() => {
    loadDashboardData();
    loadOnlineDrivers();
    setupAutoRefresh();

    return () => {
      clearAutoRefresh();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    ongoingRidesCount,
    completeRidesCount,
    onlineDriversCount,
    totalDriver,
    isLoading,
    isSpinner
  } = state;

  if (state.isSpinner) {
    return (
      <div>
        <Navbar flag={false} brandText={t("Dashboards")} />
        <LoadingSkeleton lines={4} />
      </div>
    );
  }

  return (
    <div>
      <Navbar flag={false} brandText={t("Dashboards")} />
      <OnboardingOverlay
        isActive={onboarding.isActive}
        step={onboarding.currentStep}
        currentIndex={onboarding.currentIndex}
        totalSteps={onboarding.totalSteps}
        onNext={onboarding.next}
        onPrevious={onboarding.previous}
        onSkip={onboarding.skip}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Card extra={"w-full pb-10 p-4 h-full"}>
            <div className="mb-4 flex items-center justify-end">
              <OnboardingReplayButton onReplay={onboarding.replay} />
            </div>
            <div
              id="dashboard-kpi-cards"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "25px",
              }}
            >
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                  marginRight: "10px",
                }}
              >
                <div
                  className="m-5"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div>
                    <img
                      src={RevenueIcon}
                      alt={t("Ongoing")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Ongoing")}
                    </h1>
                  </div>
                  <h3 className="mt-12 text-end" style={{ color: "#2BB180" }}>
                    {isSpinner ? (
                      <CustomSpinner />
                    ) : (
                      <span
                        style={{ fontSize: 33, cursor: "pointer" }}
                        onClick={handleNavigation}
                      >
                        {ongoingRidesCount}
                      </span>
                    )}
                  </h3>
                </div>
              </Card>
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                  marginRight: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  className="m-5"
                >
                  <div className="d-flex align-items-center mb-3 gap-3">
                    <img
                      src={VehiclesIcon}
                      alt={t("Drivers")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Drivers")}
                    </h1>
                  </div>

                  <div
                    className="mt-9"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div className="d-flex gap-2">
                      <h4
                        className="dark:text-white"
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      >
                        {t("Total:")}
                      </h4>
                      <h4
                        className=""
                        style={{
                          color: "#2BB180",
                          display: "inline-block",
                          verticalAlign: "middle",
                          marginLeft: "10px",
                        }}
                      >
                        {isSpinner ? (
                          <CustomSpinner />
                        ) : (
                          <Link style={{ fontSize: 32 }} to="/admin/drivers">
                            {totalDriver}
                          </Link>
                        )}
                      </h4>
                    </div>

                    <div className="d-flex gap-2">
                      <h4
                        className="dark:text-white"
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      >
                        {t("Online:")}
                      </h4>
                      <h4
                        className=""
                        style={{
                          color: "#2BB180",
                          display: "inline-block",
                          verticalAlign: "middle",
                          marginLeft: "10px",
                        }}
                      >
                        {isSpinner ? (
                          <CustomSpinner />
                        ) : (
                          <Link style={{ fontSize: 32 }} to="/admin/drivers">
                            {onlineDriversCount}
                          </Link>
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              </Card>
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  className="m-5"
                >
                  <div>
                    <img
                      src={RidesIcon}
                      alt={t("Completed")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Completed")}
                    </h1>
                  </div>
                  <h3 className="mt-12 text-end" style={{ color: "#2BB180" }}>
                    {isSpinner ? (
                      <CustomSpinner />
                    ) : (
                      <span
                        style={{ fontSize: 33, cursor: "pointer" }}
                        onClick={handleNavigationCompleteRide}
                      >
                        {completeRidesCount}
                      </span>
                    )}
                  </h3>
                </div>
              </Card>
            </div>

            <div className="mb-2 mt-8">
              <div>
                <h4
                  id="traffic"
                  className="card-title mb-2"
                  style={{ fontSize: "25px" }}
                >
                  {t("Active Drivers:")}
                </h4>
              </div>
              <div className="d-none d-md-block">
                {/* <CButtonGroup className="float-end me-3">
                    {['Map', 'Satellite'].map((value) => (
                      <CButton
                        color="outline-secondary"
                        key={value}
                        className="mx-0"
                        active={value === 'Map'}
                      >
                        {value}
                      </CButton>
                    ))}
                  </CButtonGroup> */}
              </div>
            </div>

            <div id="dashboard-map" className="h-100 w-100 bg-info">
              <LeafletMapComponent
                center={center}
                zoom={11}
                markers={state.allOnlineDrivers.map((driver, index) => ({
                  id: `driver-${driver.id || index}`,
                  position: {
                    lat: driver.liveLocation?.[1] || center.lat,
                    lng: driver.liveLocation?.[0] || center.lng,
                  },
                  title: `${driver.firstName} ${driver.lastName}`,
                  description: `Status: ${driver.status}`,
                  icon: car
                }))}
                style={{ width: "100%", height: "500px" }}
              />
            </div>

            {/* Segunda linha de cards: Riders + Revenue */}
            <div
              className="mt-8"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "25px",
                gap: "10px",
              }}
            >
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  className="m-5"
                >
                  <div>
                    <img
                      src={VehiclesIcon}
                      alt={t("Riders")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Riders")}
                    </h1>
                  </div>
                  <h3 className="mt-12 text-end" style={{ color: "#00C2FF" }}>
                    {isSpinner ? (
                      <CustomSpinner />
                    ) : (
                      <Link style={{ fontSize: 33 }} to="/admin/riders">
                        {state.totalRiders}
                      </Link>
                    )}
                  </h3>
                </div>
              </Card>
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  className="m-5"
                >
                  <div>
                    <img
                      src={RevenueIcon}
                      alt={t("Revenue")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Total Revenue")}
                    </h1>
                  </div>
                  <h3 className="mt-12 text-end" style={{ color: "#8B5CF6" }}>
                    {isSpinner ? (
                      <CustomSpinner />
                    ) : (
                      <span style={{ fontSize: 33 }}>
                        R$ {state.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </h3>
                </div>
              </Card>
              <Card
                style={{
                  flex: 1,
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  className="m-5"
                >
                  <div>
                    <img
                      src={RidesIcon}
                      alt={t("Today")}
                      width={40}
                      height={40}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    <h1
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginLeft: "10px",
                      }}
                    >
                      {t("Today's Rides")}
                    </h1>
                  </div>
                  <h3 className="mt-12 text-end" style={{ color: "#F59E0B" }}>
                    {isSpinner ? (
                      <CustomSpinner />
                    ) : (
                      <span style={{ fontSize: 33 }}>{state.todayRides}</span>
                    )}
                  </h3>
                </div>
              </Card>
            </div>

            {/* Gráfico semanal */}
            <div id="dashboard-charts" className="mt-8">
              <h4
                className="card-title mb-4"
                style={{ fontSize: "25px" }}
              >
                {t("Weekly Overview")}
              </h4>
              <Card
                style={{
                  border: "1px dashed #A3A3B7",
                  borderRadius: "15px",
                  padding: "20px",
                }}
              >
                {state.weeklyData.length > 0 ? (
                  <Chart
                    type="area"
                    height={300}
                    series={[
                      {
                        name: t("Rides"),
                        data: state.weeklyData.map(d => d.rides),
                      },
                      {
                        name: t("Revenue (R$)"),
                        data: state.weeklyData.map(d => d.revenue),
                      },
                    ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        background: "transparent",
                      },
                      theme: { mode: "dark" },
                      colors: ["#00C2FF", "#8B5CF6"],
                      stroke: { curve: "smooth", width: 2 },
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 0.5,
                          opacityFrom: 0.5,
                          opacityTo: 0.1,
                        },
                      },
                      xaxis: {
                        categories: state.weeklyData.map(d =>
                          new Date(d.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric' })
                        ),
                      },
                      yaxis: [
                        { title: { text: t("Rides") } },
                        { opposite: true, title: { text: t("Revenue") } },
                      ],
                      tooltip: { theme: "dark" },
                      grid: { borderColor: "#333" },
                    }}
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t("No data available for the last 7 days")}
                  </div>
                )}
              </Card>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
